import { useHistory } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { deleteQuickBooksConnection } from 'api/quickBooks';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { GivebutterAxiosError } from 'types/error';

interface DisconnectQuickBooksDialogProps extends AlertDialog.AlertDialogRootProps {}

const DisconnectQuickBooksDialog = ({ children, ...props }: DisconnectQuickBooksDialogProps) => {
    const { accountId } = useCurrentAccount();
    const { error, success } = useToast();
    const history = useHistory();

    const { isPending: isDeletingQuickBooks, mutate: deleteQuickBooks } = useMutation({
        mutationFn: () => deleteQuickBooksConnection(accountId),
        onSuccess: () => {
            success('QuickBooks disconnected!');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.quickBooks(accountId),
            });
            history.push(`/accounts/${accountId}/settings/integrations/quickbooks`);
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data?.message ?? 'Unknown error');
        },
    });

    return (
        <AlertDialog.Root type={'error'} {...props}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title>
                            Are you sure you want to disconnect QuickBooks?
                        </AlertDialog.Title>
                        <AlertDialog.Description>
                            Disconnecting will stop all syncing of data between Givebutter and
                            QuickBooks, delete mapping rules, and remove all historical sync data
                            within Givebutter. This cannot be undone.
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Close</AlertDialog.Cancel>
                        <AlertDialog.Action
                            onClick={() => deleteQuickBooks()}
                            disabled={isDeletingQuickBooks}
                        >
                            Disconnect
                        </AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const Root = DisconnectQuickBooksDialog;
const DisconnectQuickBooksDialogTrigger = AlertDialog.Trigger;
const Trigger = DisconnectQuickBooksDialogTrigger;

export {
    Root,
    Trigger,
    //
    DisconnectQuickBooksDialog,
    DisconnectQuickBooksDialogTrigger,
};
